import { Component, type ErrorInfo, type ReactNode } from 'react';
import { de } from '@/i18n/de';
import { Button } from '@/components/Button';

interface Props {
  children: ReactNode;
}

interface State {
  failed: boolean;
}

/**
 * Letzte Rettungsleine (GDD §50 „Lade- und Fehlerzustände behandelt").
 *
 * Ein Renderfehler mitten in der Runde hinterließe sonst eine weiße Seite —
 * für eine App, die gerade am Tisch die Spielleitung macht, der schlechteste
 * denkbare Zustand. Hier bleibt wenigstens ein Weg zurück ins Spiel: neu laden,
 * und der Reconnect holt den Raum über den Session-Token zurück.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Unbehandelter Renderfehler', error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;

    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-6 bg-bg px-6 text-center text-fg">
        <span className="text-5xl" aria-hidden="true">
          🂠
        </span>
        <div>
          <h1 className="font-display text-2xl font-bold">{de.crash.title}</h1>
          <p className="mt-2 text-fg-muted">{de.crash.hint}</p>
        </div>
        <Button onClick={() => location.reload()}>{de.crash.reload}</Button>
      </div>
    );
  }
}
